import { colors } from "@/constants/mapStyles";
import { selectMarketMapKeys } from "@/features/survey/surveySelectors";
import CustomSlider from "@components/CustomSlider";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldCheckbox } from "@emerald-react/checkbox";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldInput } from "@emerald-react/input";
import { EmeraldSelect } from "@emerald-react/select";
import { EmeraldSideSheet } from "@emerald-react/side-sheet";
import { EmeraldButtonVariant } from "@emerald/nxcore";
import { useState } from "react";
import { useSelector } from "react-redux";
import MapStyleBanner from "./MapStyleBanner";

const MarkerStyleDrawer = (props: {
  openMap?: boolean;
  isMapStyle?: boolean;
  closeMap: () => void;
  markerPoint?: any;
  updateMapStyles: (styles: any) => void;
  updateRadiusRings: (rings: any) => void;
  selectedPinInfo?: { numbers: number[]; ids: string[]; type: string } | null;
}) => {
  const {
    isMapStyle,
    openMap,
    closeMap,
    updateMapStyles,
    markerPoint,
    updateRadiusRings,
    selectedPinInfo,
  } = props;
  const { properties } = markerPoint || {};
  const { markerConfig } = properties || {};
  const {
    icon,
    iconColor,
    iconSize,
    radius,
    radiusColor,
    radiusLabel,
    radiusLength,
  } = markerConfig || {};
  const [colorUpdate, setColorUpdate] = useState(iconColor || "#0976C3");
  const mapKeys = useSelector(selectMarketMapKeys);
  const [mapKeyWarning, setMapKeyWarning] = useState(false);

  const [radiusColorUpdate, setRadiusColorUpdate] = useState(
    radiusColor || "#0976C3",
  );
  const [value, setValue] = useState<number | string | Array<number | string>>(
    iconSize || 30,
  );
  const [isRadiusLabel, setIsRadiusLabel] = useState(radiusLabel || false);
  const [radiusLengthState, setRadiusLengthState] = useState(
    radiusLength || "MI",
  );
  const [radiusRings, setRadiusRings] = useState<any[]>(radius || []);

  const checkForColorExist = (pickedColor: string) => {
    const isColorExist = mapKeys?.find(
      (key) =>
        key.color.toLowerCase() === pickedColor.toLowerCase() &&
        key.isCurrentLocation === (icon?.includes("star") ?? false),
    );
    if (isColorExist) {
      setMapKeyWarning(mapKeys.filter((key) => key.active).length > 8);
    } else {
      setMapKeyWarning(mapKeys.filter((key) => key.active).length > 7);
    }
  };

  const handleColorChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newColor = event.target.value;
    if (newColor.length !== 7) return;
    setColorUpdate(newColor || "#0976C3");
    checkForColorExist(newColor);
    if (/^#[0-9A-F]{6}$/i.test(newColor)) {
      updateMapStyles({ color: newColor, size: value });
    } else {
      console.error("Invalid hex color code");
    }
  };

  const handleColorPick = (pickedColor: string, changeType: string) => {
    if (changeType === "mapStyle") {
      checkForColorExist(pickedColor);
      setColorUpdate(pickedColor);
      updateMapStyles({ color: pickedColor, size: value });
    } else {
      setRadiusColorUpdate(pickedColor);
      updateRadiusRings({
        radiusLabel: isRadiusLabel,
        radiusColor: pickedColor,
        radiusLength: radiusLengthState,
        radius: radiusRings,
      });
    }
  };

  const handleSliderChange = (newValue: number) => {
    setValue(newValue);
    updateMapStyles({ color: colorUpdate, size: newValue });
  };

  const handleRings = () => {
    setRadiusRings(radiusRings.concat({ value: 0 }));
    updateRadiusRings({
      radiusLabel: isRadiusLabel,
      radiusColor: radiusColorUpdate,
      radiusLength: radiusLengthState,
      radius: radiusRings.concat({ value: 0 }),
    });
  };

  const handleDeleteRing = (index: number) => () => {
    const updatedRings = radiusRings.filter((_, i) => i !== index);
    setRadiusRings(updatedRings);
    updateRadiusRings({
      radiusLabel: isRadiusLabel,
      radiusColor: radiusColorUpdate,
      radiusLength: radiusLengthState,
      radius: updatedRings,
    });
  };

  const handleRadiusLengthChange = (e: any) => {
    const value = e.target.innerHTML === "Miles" ? "MI" : "KM";
    setRadiusLengthState(value);
    updateRadiusRings({
      radiusLabel: isRadiusLabel,
      radiusColor: radiusColorUpdate,
      radiusLength: value,
      radius: radiusRings,
    });
  };

  const handleRadiusRing = (e: any, index: number) => {
    const updatedRings = radiusRings.map((ring, i) => {
      if (i === index) {
        return { ...ring, value: e.target.value };
      }
      return ring;
    });
    setRadiusRings(updatedRings);
    updateRadiusRings({
      radiusLabel: isRadiusLabel,
      radiusColor: radiusColorUpdate,
      radiusLength: radiusLengthState,
      radius: updatedRings,
    });
  };

  const handleLabelChange = (value: boolean) => {
    setIsRadiusLabel(value);
    updateRadiusRings({
      radiusLabel: value,
      radiusColor: radiusColorUpdate,
      radiusLength: radiusLengthState,
      radius: radiusRings,
    });
  };

  const colorBox = (changeType: string) => (
    <div className="flex flex-col gap-4">
      <p>Select a color</p>
      {Object.keys(colors).map((header) => (
        <div key={header} className="flex flex-col gap-2 mr-13">
          <p className="text-xs">{header}</p>
          <ul className="flex gap-2 flex-wrap">
            {colors[header].map((color: string) => (
              <li
                key={color}
                onClick={() => handleColorPick(color, changeType)}
                className="w-6 h-6 rounded-xl min-w-auto cursor-pointer flex"
                style={{ backgroundColor: color }}
              />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  return (
    <EmeraldSideSheet
      opened={openMap}
      title={isMapStyle ? "Style Points" : "Radius Rings"}
      position={"right"}
      closeOnOutsideClick={false}
      hasCloseButton={true}
      closeOnEscape={true}
      className="mapStyleDrawer"
      hasDivider={true}
      onClose={closeMap}
    >
      {isMapStyle ? (
        <>
          <div className="flex flex-col gap-4 text-data">
            <MapStyleBanner isWarning={mapKeyWarning} selectedPinInfo={selectedPinInfo} />
            <div className="flex flex-col gap-2">
              <p className="text-base">Select a point size</p>
              <p className="text-sm">Enter a value between 10 and 200</p>
              <div className="mb-4">
                <CustomSlider
                  min={10}
                  max={200}
                  step={1}
                  value={Number(Array.isArray(value) ? value[0] : value)}
                  onChange={handleSliderChange}
                  showInput={true}
                  label="Size"
                />
              </div>
            </div>
            {colorBox("mapStyle")}
            <div className="flex flex-col gap-3">
              <p className="text-xs">Custom Color</p>
              <div>
                <EmeraldInput
                  type="text"
                  label={"Enter a hex color code"}
                  value={colorUpdate}
                  inputSize={"medium"}
                  isValid={true}
                  className={"without-label"}
                  onChange={handleColorChange}
                  maxLength={7}
                />
                <p className={`bg-${colorUpdate} w-6 h-6 rounded-full`}></p>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-3 text-data">
          <p className="text-xs mb-2">Measure distance in</p>
          <EmeraldSelect
            label={"Miles"}
            title={"Emerald"}
            value={radiusLengthState}
            onChange={handleRadiusLengthChange}
            outlined
            options={[
              {
                label: "Miles",
                value: "MI",
              },
              {
                label: "Kilometers",
                value: "KM",
              },
            ]}
          />
          <EmeraldCheckbox
            label="Show distance labels"
            onChange={handleLabelChange}
            checked={isRadiusLabel}
          />
          {radiusRings?.map((ring, index) => (
            <div
              key={`${ring} _${index}`}
              className="flex justify-between items-center"
            >
              <p className="text-sm">{index + 1}. Ring Distance</p>
              <div className="flex gap-3 items-center">
                <EmeraldInput
                  type="number"
                  value={ring?.value || 0}
                  inputSize={"medium"}
                  className={"without-label rings-input text-data"}
                  onChange={(e) => handleRadiusRing(e, index)}
                  min={0}
                  onKeyDown={(e) => {
                    if (
                      e.key === "-" ||
                      e.key === "e" ||
                      e.key === "E" ||
                      e.key === "+"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
                <EmeraldIcon
                  icon="delete"
                  type="filled"
                  variant="icon"
                  className=" text-data-light"
                  onClick={handleDeleteRing(index)}
                />
              </div>
            </div>
          ))}
          <EmeraldButton
            type={EmeraldButtonVariant.Secondary}
            onClick={handleRings}
            className="w-full mb-2 rounded-sm addRadiusRing"
          >
            <EmeraldIcon icon="add" type="filled" variant="icon" />{" "}
            <span>Add Ring</span>
          </EmeraldButton>
          {colorBox("radiusRings")}
        </div>
      )}
    </EmeraldSideSheet>
  );
};

export default MarkerStyleDrawer;
